import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import {
	MenuConfigurationDTO,
	MenuGroupConfigurationDTO,
	PrintPreviewDTO,
	Res
} from 'core/api';
import { Col, Cols, Flex } from 'core/ui';
import { MenuList } from './configureMenuCommand';
import { CatalogueFacade } from 'pos/facades';
import * as MenuConfiguration from './MenuConfiguration';
import { Message, Modal } from 'core/ui-next/modal';

function MultipleMenuGroupList({ groups, selectedGroups, onSelectGroup }: {
	groups: MenuGroupConfigurationDTO[];
	selectedGroups: MenuGroupConfigurationDTO[];
	onSelectGroup: (group: MenuGroupConfigurationDTO) => void;
}): React.ReactNode {
	const groupList = groups.map(g => {
		const selectedClass = selectedGroups.some(sg => sg.ItemsCategoryId === g.ItemsCategoryId) ? 'selected' : '';
		return <li className={selectedClass} key={g.ItemsCategoryId} onClick={() => { onSelectGroup(g); }}>
			<Cols>
				<Col className='text-left text-18'>
					{g.GroupName}
				</Col>
			</Cols>
		</li>;
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header'>
				<Col>
					{Res.Web.CONFIGURE_MENU_GROUPS_LBL()}
				</Col>
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul className='list'>
				{groupList}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}


type Props = { menus: MenuConfigurationDTO[], printers: string[], selectedMenu: MenuConfigurationDTO } & Modal.Actions<void>;

type State = {
	copies: number,
	printer: string,
	preview: Nullable<PrintPreviewDTO>,
	menu: MenuConfigurationDTO
};

class PrintMenuScreen extends PosScreenClassComponent<Props, State> {

	createInitialState(props: Props): Nullable<State> {
		return { copies: 1, menu: MenuConfiguration.selectMenu(props.selectedMenu), preview: null, printer: props.printers[0] };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	private form = React.createRef<UI.Form<State>>();

	onSelectMenu = async (menu: MenuConfigurationDTO) => {
		this.setState({ menu: MenuConfiguration.selectMenu(menu), preview: null });
	};

	onPrinterChange = async (printers: string[]) => {
		this.setState({ printer: printers[0] });
	};

	onToggleSelectGroup = async (group: MenuGroupConfigurationDTO) => {
		const menu = MenuConfiguration.addOrRemoveGroup(this.state.menu, group);
		const preview = await CatalogueFacade.getMenuPrintPreview(menu);
		this.setState({ menu, preview });
	};


	onPrintMenu = async () => {
		if (this.state.menu.MenuGroups.length === 0) {
			await Message.show(Res.Msg.MSG_NO_MENU_GROUP_SELECTED());
			return;
		}

		const isValid = await this.form.current!.updateModel();
		if (isValid) {
			const formState = this.form.current!.model;

			await CatalogueFacade.printMenu(this.state.menu, formState.printer, formState.copies);
			this.cancel();
		}
	};

	protected getScreenId(): string {
		return 'PRINT_MENU';
	}

	protected getTitle(): string {
		return Res.Web.PRINT_MENU();
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getImage(): string | SvgIcon {
		return Images.PrintTicket;
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const printBtn = { title: Res.Web.PRINT_MENU_BTN(), image: Images.PrintTicket, action: this.onPrintMenu };
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			printBtn
		];
	}

	protected getContent(): React.ReactNode {

		const { Form, IntBox, ComboBox } = UI.controlsFor<State>();
		const allMenuGroups = this.props.menus.find(x => x.MenuId === this.state.menu.MenuId)?.MenuGroups ?? [];
		const selectedGroups = this.state.menu.MenuGroups;
		return <Flex.Vertical>
			<Flex.Item className='p-t-md p-l-md text-18'>
				{Res.Web.PRINT_MENU_SELECT_MENU_AND_GROUPS_LBL()}
			</Flex.Item>
			<Flex.Item size='*'>
				<Flex.Horizontal>
					<Flex.Item size='*' className='p-m'>
						<MenuList menus={this.props.menus} selectedMenu={this.state.menu} onSelectMenu={this.onSelectMenu} />
					</Flex.Item>
					<Flex.Item size='*' className='p-m'>
						<MultipleMenuGroupList groups={allMenuGroups} selectedGroups={selectedGroups} onSelectGroup={this.onToggleSelectGroup} />
					</Flex.Item>
					<Flex.Item className='p-m' size='432px'>
						<div className='list-header cols p-xxs'>
							{Res.Web.PRINT_MENU_PREVIEW_LBL()}
						</div>
						<Flex.Vertical>
							<Flex.Item size='*' >
								{
									this.state.preview !== null
										? <UI.TemplatePrintPreview preview={this.state.preview} />
										: <div></div>
								}
							</Flex.Item>
							<Flex.Item className='m-t m-b-lg'>

								<Form height='full' ref={this.form} model={this.state} className=''>
									<IntBox
										linkTo={x => x.copies}
										label={Res.Web.PRINT_MENU_COPIES_QUANTITY_LBL()}
										allowNegative={false}
										rules={UI.Constraints.range(1, 99)}
										required
									/>
									<ComboBox
										linkTo={x => x.printer}
										label={Res.Web.PRINT_MENU_PRINTER_LBL()}
										options={this.props.printers}
										onValueChanged={this.onPrinterChange}
										required
									/>
								</Form >
							</Flex.Item>
						</Flex.Vertical>
					</Flex.Item>
				</Flex.Horizontal>
			</Flex.Item>
		</Flex.Vertical>;
	}
}

declare global {
	interface CommandType {
		printMenu: (menus: MenuConfigurationDTO[], selectedMenu: MenuConfigurationDTO) => Promise<Result>
	}
}

Command.register('printMenu', Preconditions.always, async (menus: MenuConfigurationDTO[], selectedMenu: MenuConfigurationDTO): Promise<Result> => {
	const printers = await CatalogueFacade.getInstalledPrinters();
	return await Modal.show(PrintMenuScreen, { menus, selectedMenu, printers });
});


